import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Observable, from, of } from "rxjs";
import { catchError, concatMap, map, toArray } from "rxjs/operators";
import Swal from "sweetalert2";

import { MantenimientoService } from "../mantenimiento.service";
import {
  FOTO_MAX_MB,
  FOTOS_ACEPTADAS,
  FOTOS_MAX_POR_ETAPA,
  Mantenimiento,
  MantenimientoFoto,
  MantenimientoHistorial,
  MantenimientoRes,
  categoriaDe,
  estatusDe,
  urgenciaDe,
} from "../../shared/model/mantenimiento/mantenimiento.model";

export interface DetalleMantenimientoData {
  idSolicitud: number;
}

const ABIERTAS = ["SOLICITADA", "EN_ATENCION", "EN_ESPERA"];

/**
 * El detalle de una solicitud de mantenimiento, para quien la pidió y para
 * quien la atiende. Los botones salen según quién la abre y en qué estatus
 * está; el servidor vuelve a revisar todo (MantenimientoBuss), así que un
 * botón de más aquí no le da a nadie un permiso que no tiene.
 *
 * Se cierra con `true` si algo cambió, para que la lista se refresque.
 */
@Component({
  selector: "app-detalle-mantenimiento",
  standalone: true,
  templateUrl: "./detalle-mantenimiento.component.html",
  styleUrls: ["../mantenimiento-comun.css", "./detalle-mantenimiento.component.css"],
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule],
})
export class DetalleMantenimientoComponent implements OnInit, OnDestroy {
  readonly ACEPTADAS = FOTOS_ACEPTADAS;

  s: Mantenimiento | null = null;
  historial: MantenimientoHistorial[] = [];
  fotos: MantenimientoFoto[] = [];
  responsables: Mantenimiento[] = [];
  puedeAtender = false;
  idYo: number | null = null;
  diasCierre = 5;

  cargando = true;
  error: string | null = null;
  trabajando = false;
  comentario = "";

  /** Las miniaturas, ya como URL del blob (el archivo pide token). */
  vistas = new Map<number, string>();
  /** La foto abierta en grande. */
  grande: MantenimientoFoto | null = null;

  /** Las fotos de quien pidió y las de quien atiende, ya separadas. */
  grupos: { titulo: string; lista: MantenimientoFoto[] }[] = [];

  private cambio = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DetalleMantenimientoData,
    private ref: MatDialogRef<DetalleMantenimientoComponent, boolean>,
    private service: MantenimientoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  ngOnDestroy(): void {
    this.vistas.forEach((u) => URL.revokeObjectURL(u));
  }

  cerrar(): void {
    this.ref.close(this.cambio);
  }

  /* ================= Carga ================= */

  private cargar(fondo = false): void {
    if (!fondo) {
      this.cargando = true;
    }
    this.service.detalle(this.data.idSolicitud, fondo).subscribe({
      next: (res) => this.pinta(res),
      error: () => {
        this.cargando = false;
        this.error = "No se pudo conectar con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  private pinta(res: MantenimientoRes): void {
    this.cargando = false;
    if (res?.codigoError || !res?.solicitud) {
      this.error = res?.mensajeError || "No se pudo abrir la solicitud.";
      this.cdr.detectChanges();
      return;
    }
    this.error = null;
    this.s = res.solicitud;
    this.historial = res.historial || [];
    this.fotos = res.fotos || [];
    this.grupos = [
      { titulo: "Cómo estaba", lista: this.fotosSolicitud },
      { titulo: "Cómo quedó", lista: this.fotosAtencion },
    ];
    this.responsables = res.responsables || [];
    this.puedeAtender = !!res.puedeAtender;
    this.idYo = res.idYo ?? null;
    this.diasCierre = res.diasCierre || 5;
    this.cargaVistas();
    this.cdr.detectChanges();
  }

  /** Baja las fotos que falten; las que ya no están se sueltan. */
  private cargaVistas(): void {
    const ids = new Set(this.fotos.map((f) => f.idFoto!));
    this.vistas.forEach((u, id) => {
      if (!ids.has(id)) {
        URL.revokeObjectURL(u);
        this.vistas.delete(id);
      }
    });
    for (const f of this.fotos) {
      if (f.idFoto == null || this.vistas.has(f.idFoto)) {
        continue;
      }
      this.service.verFoto(f.idFoto).subscribe({
        next: (blob) => {
          this.vistas.set(f.idFoto!, URL.createObjectURL(blob));
          this.cdr.detectChanges();
        },
        error: () => {},
      });
    }
  }

  /* ================= Quién puede qué ================= */

  get esDueno(): boolean {
    return !!this.s && this.s.idUsuarioSolicita === this.idYo;
  }

  get esResponsable(): boolean {
    return !!this.s && this.s.idUsuarioResponsable != null && this.s.idUsuarioResponsable === this.idYo;
  }

  get abierta(): boolean {
    return !!this.s && ABIERTAS.includes(this.s.estatus || "");
  }

  get terminada(): boolean {
    return this.s?.estatus === "CERRADA" || this.s?.estatus === "CANCELADA";
  }

  /**
   * La tiene alguien que ya no atiende (le quitaron el perfil o lo dieron
   * de baja): cualquiera de los que atienden la puede relevar. «Los que
   * atienden» son los que manda el servidor en responsables.
   */
  get huerfana(): boolean {
    const r = this.s?.idUsuarioResponsable;
    return this.puedeAtender && r != null && !this.responsables.some((x) => x.idUsuarioResponsable === r);
  }

  get puedeTomar(): boolean {
    return this.puedeAtender && this.abierta && !this.esResponsable
      && (this.s?.idUsuarioResponsable == null || this.huerfana);
  }

  get puedeMover(): boolean {
    return this.puedeAtender && this.esResponsable && this.abierta;
  }

  /**
   * A quién se le puede pasar: los que atienden, menos quien ya la tiene y
   * menos uno mismo (para quedársela está «Tomarla»).
   */
  get otros(): Mantenimiento[] {
    return this.responsables.filter(
      (r) => r.idUsuarioResponsable !== this.s?.idUsuarioResponsable && r.idUsuarioResponsable !== this.idYo,
    );
  }

  get puedeReasignar(): boolean {
    return this.puedeAtender && this.abierta
      && (this.esResponsable || this.s?.idUsuarioResponsable == null || this.huerfana)
      && this.otros.length > 0;
  }

  get puedeConfirmar(): boolean {
    return this.esDueno && this.s?.estatus === "RESUELTA";
  }

  get puedeCancelar(): boolean {
    return this.esDueno && this.abierta;
  }

  get puedeComentar(): boolean {
    return (this.esDueno || this.puedeAtender) && !this.terminada;
  }

  get puedeSubirFoto(): boolean {
    if (this.esResponsable && this.puedeAtender) {
      return (this.abierta || this.s?.estatus === "RESUELTA") && this.fotosAtencion.length < FOTOS_MAX_POR_ETAPA;
    }
    return this.esDueno && this.abierta && this.fotosSolicitud.length < FOTOS_MAX_POR_ETAPA;
  }

  puedeQuitar(f: MantenimientoFoto): boolean {
    return f.idUsuario === this.idYo && !this.terminada;
  }

  get fotosSolicitud(): MantenimientoFoto[] {
    return this.fotos.filter((f) => f.etapa !== "ATENCION");
  }

  get fotosAtencion(): MantenimientoFoto[] {
    return this.fotos.filter((f) => f.etapa === "ATENCION");
  }

  /* ================= Etiquetas ================= */

  get categoria() {
    return categoriaDe(this.s?.categoria);
  }

  get urgencia() {
    return urgenciaDe(this.s?.urgencia);
  }

  get estatus() {
    return estatusDe(this.s?.estatus);
  }

  etiquetaEstatus(e?: string | null): string {
    return estatusDe(e).texto;
  }

  /** El día en que se cierra sola si nadie contesta. */
  get cierraEl(): string | null {
    if (this.s?.estatus !== "RESUELTA" || !this.s.fechaResuelta) {
      return null;
    }
    const d = this.fecha(this.s.fechaResuelta);
    if (!d) {
      return null;
    }
    d.setDate(d.getDate() + this.diasCierre);
    return d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
  }

  /** «2026-09-29 10:15» → «29 sep 2026, 10:15». */
  cuando(f?: string | null): string {
    const d = this.fecha(f);
    if (!d) {
      return f || "";
    }
    const dia = d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" }).replace(".", "");
    const hora = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${dia}, ${hora}`;
  }

  private fecha(f?: string | null): Date | null {
    if (!f) {
      return null;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(f);
    return m ? new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) : null;
  }

  iconoHistoria(h: MantenimientoHistorial): string {
    switch (h.accion) {
      case "ALTA": return "add_circle";
      case "ASIGNACION": return "person_add";
      case "REAPERTURA": return "replay";
      case "CIERRE_AUTOMATICO": return "schedule";
      case "COMENTARIO": return "chat_bubble_outline";
      case "FOTO": return "photo_camera";
      case "CAMBIO_ESTATUS": return estatusDe(h.estatusNuevo).icono;
      default: return "circle";
    }
  }

  claseHistoria(h: MantenimientoHistorial): string {
    if (h.accion === "CAMBIO_ESTATUS" || h.accion === "REAPERTURA" || h.accion === "CIERRE_AUTOMATICO") {
      return estatusDe(h.estatusNuevo).clase;
    }
    return h.accion === "COMENTARIO" ? "es-comentario" : "es-evento";
  }

  /* ================= Acciones de quien atiende ================= */

  tomar(): void {
    this.hace(this.service.tomar(this.s!.idSolicitud!), "Ya es tuya: está en atención.");
  }

  mover(destino: "EN_ESPERA" | "EN_ATENCION" | "RESUELTA" | "CANCELADA"): void {
    const textos = {
      EN_ESPERA: { titulo: "¿Qué se está esperando?", ph: "Ej. La refacción llega el jueves.", btn: "Poner en espera" },
      EN_ATENCION: { titulo: "¿Qué cambió?", ph: "Ej. Ya llegó el material.", btn: "Retomar" },
      RESUELTA: { titulo: "¿Qué se hizo?", ph: "Quien la pidió va a leer esto y confirmar.", btn: "Dar por resuelta" },
      CANCELADA: { titulo: "¿Por qué no procede?", ph: "Ej. Es de sistemas: que levante un ticket.", btn: "No procede" },
    }[destino];
    Swal.fire({
      title: textos.titulo,
      input: "textarea",
      inputPlaceholder: textos.ph,
      inputAttributes: { maxlength: "2000" },
      showCancelButton: true,
      confirmButtonText: textos.btn,
      cancelButtonText: "Cancelar",
      confirmButtonColor: destino === "CANCELADA" ? "#b91c1c" : undefined,
      inputValidator: (v) => (v && v.trim() ? null : "Escribe una línea: queda en la historia."),
    }).then((r) => {
      if (r.isConfirmed) {
        const ok = {
          EN_ESPERA: "Quedó en espera.",
          EN_ATENCION: "De vuelta en atención.",
          RESUELTA: "Resuelta: ahora le toca confirmar a quien la pidió.",
          CANCELADA: "Cerrada: no procede.",
        }[destino];
        this.hace(this.service.mover(this.s!.idSolicitud!, destino, r.value), ok);
      }
    });
  }

  reasignar(): void {
    const opciones: Record<string, string> = {};
    this.otros.forEach((o) => (opciones[String(o.idUsuarioResponsable)] = o.responsable || ""));
    Swal.fire({
      title: "¿A quién se la pasas?",
      input: "select",
      inputOptions: opciones,
      inputPlaceholder: "Escoge a alguien",
      showCancelButton: true,
      confirmButtonText: "Pasársela",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (v ? null : "Escoge a alguien."),
    }).then((r) => {
      if (r.isConfirmed) {
        this.hace(this.service.reasignar(this.s!.idSolicitud!, Number(r.value)), "Listo, ya la tiene.");
      }
    });
  }

  /* ================= Acciones de quien la pidió ================= */

  confirmar(): void {
    Swal.fire({
      title: "¿Quedó bien?",
      text: "Se cierra la solicitud. Si quieres, deja un comentario.",
      input: "textarea",
      inputPlaceholder: "Opcional",
      inputAttributes: { maxlength: "2000" },
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, quedó bien",
      cancelButtonText: "Todavía no",
    }).then((r) => {
      if (r.isConfirmed) {
        this.hace(this.service.confirmar(this.s!.idSolicitud!, r.value || null), "¡Gracias! Quedó cerrada.");
      }
    });
  }

  reabrir(): void {
    Swal.fire({
      title: "¿Qué sigue mal?",
      input: "textarea",
      inputPlaceholder: "Cuéntale a quien la atendió qué falta.",
      inputAttributes: { maxlength: "2000" },
      showCancelButton: true,
      confirmButtonText: "Reabrir",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (v && v.trim() ? null : "Cuenta qué sigue mal."),
    }).then((r) => {
      if (r.isConfirmed) {
        this.hace(this.service.reabrir(this.s!.idSolicitud!, r.value), "Reabierta: vuelve a atención.");
      }
    });
  }

  cancelar(): void {
    Swal.fire({
      title: "¿Retirar la solicitud?",
      text: "Ya no se va a atender. Si quieres, di por qué.",
      input: "textarea",
      inputPlaceholder: "Opcional",
      inputAttributes: { maxlength: "2000" },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, retirar",
      cancelButtonText: "No",
      confirmButtonColor: "#b91c1c",
    }).then((r) => {
      if (r.isConfirmed) {
        this.hace(this.service.cancelar(this.s!.idSolicitud!, r.value || null), "Solicitud retirada.");
      }
    });
  }

  /* ================= Comentarios y fotos ================= */

  comentar(): void {
    const texto = this.comentario.trim();
    if (!texto || this.trabajando) {
      return;
    }
    this.hace(this.service.comentar(this.s!.idSolicitud!, texto), null, () => (this.comentario = ""));
  }

  subirFotos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivos = Array.from(input.files || []);
    input.value = "";
    if (!archivos.length || !this.s) {
      return;
    }
    const buenas = archivos.filter((a) => a.size <= FOTO_MAX_MB * 1024 * 1024);
    const grandes = archivos.length - buenas.length;
    this.trabajando = true;
    from(buenas)
      .pipe(
        concatMap((a) =>
          this.service.subirFoto(this.s!.idSolicitud!, a).pipe(
            map((r) => (r?.codigoError ? `${a.name}: ${r.mensajeError || "no se pudo subir"}` : null)),
            catchError(() => of(`${a.name}: no se pudo conectar`)),
          ),
        ),
        toArray(),
      )
      .subscribe((res) => {
        this.trabajando = false;
        const fallas = res.filter((x): x is string => !!x);
        if (grandes) {
          fallas.push(`${grandes === 1 ? "Una foto pesa" : grandes + " fotos pesan"} más de ${FOTO_MAX_MB} MB`);
        }
        if (fallas.length) {
          Swal.fire({ icon: "warning", title: "Algunas fotos no se subieron",
            html: '<div style="font-size:13.5px;color:#475569;text-align:left">' +
              fallas.map((f) => this.escapa(f)).join("<br>") + "</div>" });
        }
        if (fallas.length < archivos.length) {
          this.cambio = true;
        }
        this.cargar(true);
      });
  }

  quitarFoto(f: MantenimientoFoto, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      title: "¿Quitar la foto?",
      text: f.nombreArchivo || "",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Quitar",
      cancelButtonText: "No",
    }).then((r) => {
      if (r.isConfirmed) {
        this.hace(this.service.quitarFoto(f.idFoto!), null);
      }
    });
  }

  verGrande(f: MantenimientoFoto): void {
    if (f.idFoto != null && this.vistas.has(f.idFoto)) {
      this.grande = f;
    }
  }

  /* ================= Apoyo ================= */

  /** Manda la operación, avisa y vuelve a cargar el detalle. */
  private hace(op: Observable<MantenimientoRes>, ok: string | null, despues?: () => void): void {
    this.trabajando = true;
    op.subscribe({
      next: (res) => {
        this.trabajando = false;
        if (res?.codigoError) {
          Swal.fire({ icon: "error", text: res.mensajeError || "No se pudo hacer el cambio." });
          // Lo más común es que alguien se adelantó: se muestra cómo quedó.
          this.cargar(true);
          this.cdr.detectChanges();
          return;
        }
        this.cambio = true;
        despues?.();
        if (ok) {
          Swal.fire({ icon: "success", text: ok, timer: 1800, showConfirmButton: false });
        }
        this.cargar(true);
      },
      error: () => {
        this.trabajando = false;
        Swal.fire({ icon: "error", text: "No se pudo conectar con el servidor." });
        this.cdr.detectChanges();
      },
    });
  }

  private escapa(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
  }

  trackHist(_: number, h: MantenimientoHistorial): number | undefined {
    return h.idHistorial;
  }

  trackFoto(_: number, f: MantenimientoFoto): number | undefined {
    return f.idFoto;
  }
}
